import {
  AtoNDefinitionWithDate,
  AtonHost,
  useFetchAllAtons,
  useFetchAtonHostsWithState,
} from '@ais-ui/api';
import { useMemo } from 'react';
import { AtoNDefinition } from '@ais-ui/api-spec/config';

export type UnifiedAtonItem = {
  id: string;
  name: string;
  type: 'physical' | 'virtual' | 'synthetic' | 'host' | 'unknown';
  owner?: string;
  coordinates?: [number, number];
  ipAddress?: string;
  siteType?: string;
  isOffline?: boolean;
  state?: number;
  mmsi?: number;
  originalData: AtoNDefinitionWithDate | AtonHost;
};

const unifiedAtonTypeFromAtonDefinitionType = (
  type: number
): 'physical' | 'virtual' | 'synthetic' | 'unknown' => {
  switch (type) {
    case 0:
      return 'physical';
    case 1:
      return 'virtual';
    case 2:
      return 'synthetic';
    default:
      return 'unknown';
  }
};

const normalizeAtonData = (
  atonRecords: AtoNDefinitionWithDate[] = [],
  atonHostsWithState: AtonHost[] = [] // Use the hosts with state data
): UnifiedAtonItem[] => {
  const normalizedRecords: UnifiedAtonItem[] = atonRecords.map((record) => ({
    id: record.uuid as string,
    name: record.name as string,
    type: unifiedAtonTypeFromAtonDefinitionType(record.virtual as number),
    owner: record.owner,
    coordinates: record?.position?.coordinates as [number, number],
    state: record.state,
    mmsi: record.mmsi,
    originalData: record,
  }));

  // Helper function to map host service states to unified state values
  const mapHostStateToUnifiedState = (host: AtonHost): number => {
    if (!host.serviceStates) {
      return 0; // Unknown state
    }

    // Critical services = state 3 (off position extended duration / critical)
    if (host.serviceStates.critical > 0) {
      return 3;
    }

    // Warning services = state 2 (off position short duration / warning)
    if (host.serviceStates.warning > 0) {
      return 2;
    }

    // Only OK services = state 1 (on position / healthy)
    if (host.serviceStates.ok > 0) {
      return 1;
    }

    // Unknown/pending services = state 0 (unknown)
    return 0;
  };

  const normalizedHosts: UnifiedAtonItem[] = atonHostsWithState.map((host) => ({
    id: host.id,
    name: host.title,
    type: 'host' as const,
    ipAddress: host.ipAddress,
    siteType: host.siteType,
    isOffline: host.isOffline,
    state: mapHostStateToUnifiedState(host), // Map service states to unified state
    originalData: host, // This now includes serviceStates
  }));

  return [...normalizedRecords, ...normalizedHosts];
};

const filterUnifiedAtons = (
  atons: UnifiedAtonItem[],
  search: string
): UnifiedAtonItem[] => {
  if (!search.trim()) return atons;

  const searchLower = search.toLowerCase();
  return atons.filter(
    (aton) =>
      aton.name.toLowerCase().includes(searchLower) ||
      aton.owner?.toLowerCase().includes(searchLower) ||
      aton.siteType?.toLowerCase().includes(searchLower)
  );
};

// Hook return type
type UseUnifiedAtonsReturn = {
  filteredAtons: UnifiedAtonItem[];
  unifiedAtons: UnifiedAtonItem[];
  isLoading: boolean;
  hasNoData: boolean;
  error?: unknown;
};

export const useUnifiedAtons = (search: string): UseUnifiedAtonsReturn => {
  const {
    data: atonData,
    isLoading: isLoadingRecords,
    error: recordsError,
  } = useFetchAllAtons();

  const {
    data: atonHostsWithState,
    isLoading: isLoadingHostsWithState,
    error: hostsWithStateError,
  } = useFetchAtonHostsWithState();

  // Combine and normalize the data using hosts with state
  // Pass empty array if atonHostsWithState is undefined/null to ensure graceful handling
  const unifiedAtons = useMemo(() => {
    return normalizeAtonData(atonData?.records, atonHostsWithState || []);
  }, [atonData?.records, atonHostsWithState]);

  // Filter the unified data
  const filteredAtons = useMemo(() => {
    return filterUnifiedAtons(unifiedAtons, search);
  }, [unifiedAtons, search]);

  // Only consider loading if the main aton data is loading
  // Host state data is optional, so we don't wait for it
  const isLoading = isLoadingRecords;
  const hasNoData = unifiedAtons.length === 0;

  // Only include recordsError since host state data is optional
  const error = recordsError;

  return {
    filteredAtons,
    unifiedAtons,
    isLoading,
    hasNoData,
    error,
  };
};
